import React, { useEffect, useMemo, useState } from 'react';
import type { FC } from 'react';

import axios from 'axios';

import { Panel } from '@shared/components/bootstrap/panel';
import { Pluralize } from '@shared/components/helpers/pluralize';

import { Titleize } from '@admin/components/helpers/titleize';
import { InventoryPalletRow } from '@admin/components/inventory/pallet_row';
import {
  activeStats,
  computeInventoryStats,
  filterInventoryByState,
  linkItemGroups,
} from '@admin/components/inventory/stats';
import type { InventoryResponse } from '@admin/components/inventory/types';
import { ItemsGrid } from '@admin/components/items/items_grid';
import { PalletSection } from '@admin/components/pallets/section';
import { StorageSnapshotButton } from '@admin/components/storage_snapshot_button/snapshot_button';
import type { IPallet } from '@admin/types/pallet';

const ROLE_CLASSES =
  'show-for-role-admin show-for-role-manager show-for-role-teamlead show-for-role-l2_agent show-for-role-dispatcher show-for-role-care';

const LoadingSpinner: FC = () => (
  <div className="spinner">
    <div />
    <div />
    <div />
  </div>
);

export interface InventoryGridProps {
  path: string;
  accountID?: number;
  /** When true, item edit links are hidden until "Edit Categories" is clicked (order show). */
  categoryEditGating?: boolean;
}

export const InventoryGrid: FC<InventoryGridProps> = ({ path, accountID, categoryEditGating = false }) => {
  const [inventory, setInventory] = useState<InventoryResponse | null>(null);
  const [stateFilter, setStateFilter] = useState<string | undefined>();
  const [editingCategories, setEditingCategories] = useState(false);

  useEffect(() => {
    setInventory(null);
    axios.get<InventoryResponse>(path).then((response) => {
      linkItemGroups(response.data);
      setInventory(response.data);
    });
  }, [path]);

  const computedStats = useMemo(() => (inventory ? computeInventoryStats(inventory) : null), [inventory]);

  const displayInventory = useMemo(
    () => (inventory ? filterInventoryByState(inventory, stateFilter) : null),
    [inventory, stateFilter],
  );

  const summaryStats = computedStats ? activeStats(computedStats, stateFilter) : null;
  const isFullGrid = accountID !== undefined;
  const categoryBulkEditMode = categoryEditGating ? editingCategories : undefined;

  if (!inventory) {
    return (
      <div className="inventory-container">
        <LoadingSpinner />
      </div>
    );
  }

  return (
    <div className="inventory-container">
      {isFullGrid && computedStats && (
        <div className="tab-base">
          <ul id="inventory-nav-tabs" className="nav nav-tabs">
            <li className={stateFilter ? '' : 'active'}>
              <a
                href="#"
                onClick={(event) => {
                  event.preventDefault();
                  setStateFilter(undefined);
                }}
              >
                <span>All</span>
                <span className="badge">{computedStats.aggregate.items.count}</span>
              </a>
            </li>
            {computedStats.states.map((state) => (
              <li key={state} className={stateFilter === state ? 'active' : ''}>
                <a
                  href="#"
                  onClick={(event) => {
                    event.preventDefault();
                    setStateFilter(state);
                  }}
                >
                  <span>
                    <Titleize text={state} />
                  </span>
                  <span className="badge">{computedStats.stats[state].items.count}</span>
                </a>
              </li>
            ))}
          </ul>

          {summaryStats && (
            <Panel>
              <Panel.Body>
                <div className="text-center">
                  <strong>{summaryStats.items.count}</strong> {summaryStats.items.count === 1 ? 'item' : 'items'}{' '}
                  <span id="inventory-cuft">
                    ({summaryStats.items.cuft.toFixed(2)} ft<sup>3</sup>)
                  </span>{' '}
                  <span>and</span> <strong>{summaryStats.pallets.count}</strong>{' '}
                  {summaryStats.pallets.count === 1 ? 'pallet' : 'pallets'}
                  {stateFilter && (
                    <>
                      {' '}
                      <span>
                        under <strong>{stateFilter}</strong>
                      </span>
                    </>
                  )}
                  <span className="pull-right">
                    <StorageSnapshotButton accountID={String(accountID)} />{' '}
                    <a
                      className={`btn btn-default bulk-edit-link ${ROLE_CLASSES}`}
                      href={`/accounts/${accountID}/items/categorizables`}
                    >
                      Bulk Edit Categories
                    </a>{' '}
                    <a className={`btn btn-default ${ROLE_CLASSES}`} href={`/accounts/${accountID}/item_groups/new`}>
                      Add Item Group
                    </a>{' '}
                    {!editingCategories ? (
                      <button
                        type="button"
                        className={`btn btn-primary ${ROLE_CLASSES}`}
                        onClick={() => setEditingCategories(true)}
                        title="Keyboard Shortcut: Hit 'Enter' to jump to next pending input"
                      >
                        Edit Categories
                      </button>
                    ) : (
                      <button
                        type="button"
                        className="btn btn-default btn-warning"
                        onClick={() => setEditingCategories(false)}
                      >
                        Leave Edit Mode
                      </button>
                    )}
                  </span>
                </div>
              </Panel.Body>
            </Panel>
          )}
        </div>
      )}

      {displayInventory?.pallets.map(
        (pallet) =>
          pallet.items.length > 0 && (
            <div key={pallet.id}>
              {isFullGrid ? <InventoryPalletRow pallet={pallet} /> : <PalletSection pallet={pallet as IPallet} />}
              <ItemsGrid items={pallet.items} isSizeable={isFullGrid} categoryBulkEditMode={categoryBulkEditMode} />
            </div>
          ),
      )}

      {(displayInventory?.other.length ?? 0) > 0 && (
        <div>
          <Panel>
            <Panel.Body>
              <strong>{isFullGrid ? 'Other' : 'Unpalletized'}</strong>
              {isFullGrid && (
                <div className="pull-right">
                  <Pluralize count={displayInventory!.other.length} singular="Item" plural="Items" />
                </div>
              )}
            </Panel.Body>
          </Panel>
          <ItemsGrid
            items={displayInventory!.other}
            isSizeable={isFullGrid}
            categoryBulkEditMode={categoryBulkEditMode}
          />
        </div>
      )}
    </div>
  );
};
